import React, { useState } from 'react';
import { Plus, Edit3, Trash2, Camera } from 'lucide-react';
import { authAxios } from '@/lib/api';
import ProductEditor from './ProductEditor';

function ProductCard({ product, onEdit, onDelete }) {
  const grade = typeof product.specs?.grade === 'string' ? product.specs.grade.split('/')[0].trim() : '';
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift overflow-hidden group" data-testid={`admin-product-${product.id}`}>
      <div className="flex gap-4 p-4">
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 relative">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center">
              <Camera size={20} className="text-earth/20" />
            </div>
          )}
          {product.photos?.length > 1 && (
            <span className="absolute bottom-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded font-sans">{product.photos.length} photos</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold text-earth-dark">{product.name}</h3>
          <p className="text-xs text-saffron font-sans mt-0.5">{product.tagline}</p>
          <p className="text-xs text-earth/50 font-sans mt-1 line-clamp-2">{product.description}</p>
          <div className="flex gap-2 mt-2">
            {product.specs?.moq && <span className="text-[9px] uppercase bg-saffron/5 text-saffron px-1.5 py-0.5 rounded font-sans">MOQ: {product.specs.moq}</span>}
            {grade && <span className="text-[9px] uppercase bg-earth/5 text-earth/60 px-1.5 py-0.5 rounded font-sans">{grade}</span>}
          </div>
        </div>
      </div>
      <div className="flex border-t border-earth/5">
        <button onClick={onEdit} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-sans font-medium text-saffron hover:bg-saffron/5 transition-colors" data-testid={`edit-${product.id}`}>
          <Edit3 size={12} /> Edit
        </button>
        <button onClick={onDelete} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-sans font-medium text-chilli/60 hover:bg-chilli/5 transition-colors border-l border-earth/5" data-testid={`delete-${product.id}`}>
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  );
}

export default function ProductsTab({ products, onRefresh }) {
  const [editingProduct, setEditingProduct] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newProductId, setNewProductId] = useState('');

  const handleSave = async (data) => {
    await authAxios.put(`/api/products/${editingProduct.id}`, data);
    setEditingProduct(null);
    onRefresh();
  };

  const handleCreate = async (data) => {
    await authAxios.post('/api/products', { id: newProductId, ...data });
    setCreating(false);
    setNewProductId('');
    onRefresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete product "${id}"? This cannot be undone.`)) return;
    await authAxios.delete(`/api/products/${id}`);
    onRefresh();
  };

  return (
    <div className="space-y-4" data-testid="products-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-earth/50 font-sans">{products.length} products</p>
        <button onClick={() => { setCreating(true); setEditingProduct(null); }} className="flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-xs px-4 py-2.5 rounded-md hover:bg-saffron-dark transition-colors" data-testid="add-product-btn">
          <Plus size={14} /> Add Product
        </button>
      </div>

      {creating && (
        <div className="mb-4">
          <div className="bg-saffron/5 border border-saffron/20 rounded-lg p-4 mb-3">
            <label className="text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1 block">Product ID (URL slug, lowercase, no spaces)</label>
            <input value={newProductId} onChange={(e) => setNewProductId(e.target.value.toLowerCase().replace(/\s+/g, '-'))} placeholder="e.g. black-pepper" className="w-full border border-earth/15 rounded-md px-3 py-2.5 text-sm font-sans text-earth-dark focus:ring-2 focus:ring-saffron/30 focus:border-saffron focus:outline-none bg-white" data-testid="new-product-id" />
          </div>
          {newProductId && <ProductEditor onSave={handleCreate} onCancel={() => setCreating(false)} />}
        </div>
      )}

      {editingProduct && (
        <ProductEditor key={editingProduct.id} product={editingProduct} onSave={handleSave} onCancel={() => setEditingProduct(null)} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {products.map(product => (
          <ProductCard
            key={product.id}
            product={product}
            onEdit={() => { setEditingProduct(product); setCreating(false); }}
            onDelete={() => handleDelete(product.id)}
          />
        ))}
      </div>
    </div>
  );
}
